import React, { FC } from 'react';
import { Formik } from 'formik';
import cx from 'classnames';

import { SelectOption } from 'QuoteApp/services/endpoints/GetConfig';
import { useAppContext, usePurchaseContext } from 'QuoteApp/services/providers';
import { FlexiblePaymentFrequency } from 'QuoteApp/services/endpoints/GetPaymentDetails';
import { GetDetailsResponseType } from 'QuoteApp/services/endpoints/GetDetails';
import { formatText, getWeekDay, toCamelCase, toSnakeCase } from 'Shared/utils';

import { PriceCustomizerProvider } from '../PriceCustomizerContext';
import { SelectorContainer } from '../selectors';
import {
  buildBiweeklyDateSelectorData,
  buildMonthlySelectorData,
  buildSemiMonthlySelectorData,
  buildWeekdaySelectorData,
  getConfigByFrequency,
} from '../utils';
import { validationSchema } from '../schema';
import { PriceContext, PriceCustomizerFormValues, PriceState } from '../domain';
import { fromInputToOutput, fromOutputToInput } from '../mapper';
import { PlanComparison } from '../PlanComparison';
import styles from './styles.module.css';

export const PricePlanConfigurator: FC = () => {
  const {
    details,
    config,
    rate,
    fetchRate,
    refreshDetails,
    rateLoading,
    detailsLoading,
    api,
  } = useAppContext();
  const { getSignaturePacket } = usePurchaseContext();

  // Weekly default based on the effective date
  const getDefaultWeeklyPaymentDay = (effectiveStartDate: string) => {
    const effectiveDate = new Date(effectiveStartDate);

    if (isNaN(effectiveDate.getTime())) return undefined;

    const weekdayConfig = getConfigByFrequency(
      'weekly',
      config?.flexiblePayments?.paymentFrequencies
    ) as Array<SelectOption<string>>;

    const effectiveWeekDay = formatText(getWeekDay(effectiveDate), 'lowercase');

    const match = weekdayConfig.find(
      option => option.value === effectiveWeekDay
    );

    if (!match) return undefined;

    return match.value;
  };

  // Biweekly default based on the effective date
  const getDefaultBiweeklyPaymentDay = (effectiveStartDate: string) => {
    const effectiveDate = new Date(effectiveStartDate);

    if (isNaN(effectiveDate.getTime())) return undefined;

    const weekdayConfig = getConfigByFrequency(
      'biWeekly',
      config?.flexiblePayments?.paymentFrequencies
    ) as Array<SelectOption<string>>;

    const effectiveWeekDay = formatText(getWeekDay(effectiveDate), 'lowercase');

    const match = weekdayConfig.find(
      option => option.value === effectiveWeekDay
    );

    if (!match) return undefined;

    return match.value;
  };

  // Semi-monthly default based on the effective date
  const getDefaultSemiMonthlyPaymentDays = (effectiveStartDate: string) => {
    const effectiveDate = new Date(effectiveStartDate);

    if (isNaN(effectiveDate.getTime())) return undefined;

    const semiMonthlyConfig = getConfigByFrequency(
      'semiMonthly',
      config?.flexiblePayments?.paymentFrequencies
    ) as Array<SelectOption<Array<number>>>;

    const effectiveDay = effectiveDate.getUTCDate();

    // There are scenarios where could exist duplicated values
    // 1,15 and 15,29, in this example day 15 is the duplicated value,
    // if that is the case we want to return the first instance where the
    // effective day is found
    const match = semiMonthlyConfig.find(option =>
      option.value.includes(effectiveDay)
    );

    if (!match) {
      const defaultDays = [1, 15];

      const defaultValue =
        semiMonthlyConfig.find(option =>
          defaultDays.some(day => option.value.includes(day))
        )?.value ?? semiMonthlyConfig[0].value;

      return defaultValue.join(',');
    }

    return match.value.join(',');
  };

  // Monthly default based on the effective date
  const getDefaultMonthlyPaymentDay = (effectiveStartDate: string) => {
    const effectiveDate = new Date(effectiveStartDate);

    if (isNaN(effectiveDate.getTime())) return undefined;

    const monthlyConfig = getConfigByFrequency(
      'monthly',
      config?.flexiblePayments?.paymentFrequencies
    ) as Array<SelectOption<number>>;

    const effectiveDay = effectiveDate.getUTCDate();

    const match = monthlyConfig.find(option => option.value === effectiveDay);

    if (!match) return undefined;

    return match.value.toString();
  };

  const getPriceCustomizerInitialValues = (
    details: GetDetailsResponseType
  ): PriceCustomizerFormValues => {
    const { frequency, daysOfWeek, daysOfMonth } =
      details?.flexiblePaymentPreferences || {};

    const effectiveStartDate = details?.effectiveDate.value;

    const {
      frequency: initialFrequency,
      dayOfWeek = getDefaultWeeklyPaymentDay(effectiveStartDate),
      biweeklyDate = getDefaultBiweeklyPaymentDay(effectiveStartDate),
      semiMonthlyDates = getDefaultSemiMonthlyPaymentDays(effectiveStartDate),
      monthly = getDefaultMonthlyPaymentDay(effectiveStartDate),
    } = fromInputToOutput({
      frequency: toCamelCase(frequency) as FlexiblePaymentFrequency,
      daysOfWeek: daysOfWeek ? daysOfWeek.map(String) : [],
      daysOfMonth: daysOfMonth ? daysOfMonth.map(Number) : [],
    });

    return {
      frequency: initialFrequency,
      dayOfWeek,
      biweeklyDate,
      semiMonthlyDates,
      monthly,
    };
  };

  const generateSelectorInitialState = (
    formValues: PriceCustomizerFormValues
  ): {
    status: PriceState;
    context: Partial<PriceContext>;
  } => {
    const contextInitialState = getPriceCustomizerInitialValues(details);

    return {
      status: 'ready',
      context: {
        biweeklyDate:
          formValues.biweeklyDate || contextInitialState.biweeklyDate,
        dayOfMonth: formValues.monthly || contextInitialState.monthly,
        dayOfWeek: formValues.dayOfWeek || contextInitialState.dayOfWeek,
        downpayment: 0,
        frequency: formValues.frequency || contextInitialState.frequency,
        semiMonthlyDates:
          formValues.semiMonthlyDates || contextInitialState.semiMonthlyDates,
      },
    };
  };

  const buildUpdatePayload = (formValues: PriceCustomizerFormValues) => {
    const { frequency, daysOfMonth, daysOfWeek } = fromOutputToInput(
      formValues
    );

    if (frequency === 'fullPay') {
      return {
        flexiblePaymentPreferences: {
          frequency: toSnakeCase(frequency),
        },
      };
    }

    if (frequency.toLowerCase().includes('monthly')) {
      return {
        flexiblePaymentPreferences: {
          frequency: toSnakeCase(frequency),
          days_of_month: daysOfMonth ? daysOfMonth.map(Number) : [],
        },
      };
    }

    return {
      flexiblePaymentPreferences: {
        frequency: toSnakeCase(frequency),
        days_of_week: daysOfWeek ? daysOfWeek.map(String) : [],
      },
    };
  };

  const updateFlexiblePaymentPreferences = async (
    updates: Partial<PriceCustomizerFormValues>
  ) => {
    await api.saveAppData({ data: buildUpdatePayload(updates) });
    await fetchRate();
    // Refresh details to reflect the latest data generated after rate
    await refreshDetails();
    await getSignaturePacket();
  };

  return (
    <PriceCustomizerProvider rate={rate}>
      {selectedFrequency => (
        <div className={styles.flexiblePaymentTable}>
          <div className={styles.tableHeader}>
            <h2>Choose a payment plan</h2>
          </div>
          <PlanComparison
            isLoading={rateLoading || detailsLoading || api.loading}
            onPlanSelect={async frequencyName => {
              await updateFlexiblePaymentPreferences({
                ...getPriceCustomizerInitialValues(details),
                frequency: frequencyName,
              });
            }}
          />
          <div
            className={cx(styles.tableFooter, {
              [styles.hidden]: selectedFrequency === 'fullPay',
            })}
          >
            <div className={styles.footerContent}>
              <Formik
                initialValues={getPriceCustomizerInitialValues(details)}
                validationSchema={validationSchema}
                onSubmit={() => {}}
                validateOnMount
                // The button on the PlanComparison makes a selectFrequency call
                // which updates the context state outside of Formik. This allows Formik
                // to reinitialize its values when the frequency changes.
                enableReinitialize
              >
                {({ handleSubmit, values: formValues }) => {
                  return (
                    <form onSubmit={handleSubmit} noValidate>
                      <SelectorContainer
                        isLoading={rateLoading || detailsLoading || api.loading}
                        selectorsData={{
                          weekday: buildWeekdaySelectorData(
                            config?.flexiblePayments?.paymentFrequencies
                          ),
                          biweeklyDate: buildBiweeklyDateSelectorData(
                            formValues.dayOfWeek,
                            config?.effectiveDates?.startDate
                          ),
                          semiMonthly: buildSemiMonthlySelectorData(
                            config?.flexiblePayments?.paymentFrequencies
                          ),
                          monthly: buildMonthlySelectorData(
                            config?.flexiblePayments?.paymentFrequencies
                          ),
                        }}
                        initialState={generateSelectorInitialState(formValues)}
                        onSelectorChange={async props => {
                          const [[selectorName, value]] = Object.entries(props);

                          await updateFlexiblePaymentPreferences({
                            ...formValues,
                            [selectorName as keyof PriceCustomizerFormValues]: value,
                          });
                        }}
                      />
                    </form>
                  );
                }}
              </Formik>
            </div>
          </div>
        </div>
      )}
    </PriceCustomizerProvider>
  );
};
